import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { castAttributesForCamelCase, castAttributesForSnakeCase } from 'src/biz_spa/modules/string_converter'
import { useFlash } from 'src/biz_spa/composables'

export type ReadConnectsApiParams = {
  companyUuid: string
  storeUuid: string
}

export type ReadConnectApiParams = {
  companyUuid: string
  storeUuid: string
  siteType: string
}

export type CreateConnectApiParams = {
  companyUuid: string
  storeUuid: string
  siteType: string
  params: {
    review?: {
      url: string
    }
    account?: {
      identifier: string
      password: string
      storeIdentifier?: string
    }
  }
}

export type UpdateConnectApiParams = {
  companyUuid: string
  storeUuid: string
  siteType: string
  params: {
    review?: {
      url: string
    }
    account?: {
      identifier?: string
      password?: string
      storeIdentifier?: string
    }
  }
}

export type DeleteConnectApiParams = {
  companyUuid: string
  storeUuid: string
  connectType: 'accounts' | 'reviews'
  params: {
    siteType: string
  }
}

type Connect = {
  siteType: string
  review: {
    url: string | null
  }
  account: {
    identifier: string | null
    passwordLength: number | null
    storeIdentifier: string | null
  }
  meta: {
    connectableAccount: boolean
    connectableReview: boolean
    connectableAccountStoreIdentifier: boolean
  }
}

type Connects = {
  items: Connect[]
  meta: {
    accountSiteTypes: string[]
    reviewSiteTypes: string[]
    accountStoreIdentifierSiteTypes: string[]
  }
}

export const useLocationConnectStore = defineStore('locationConnect', () => {
  const { t } = useI18n()
  const { updateFlash } = useFlash()
  const connects = ref<Connects>({
    items: [],
    meta: {
      accountSiteTypes: [],
      reviewSiteTypes: [],
      accountStoreIdentifierSiteTypes: [],
    }
  })
  const connect = ref<Connect>({
    siteType: '',
    review: {
      url: null,
    },
    account: {
      identifier: null,
      passwordLength: null,
      storeIdentifier: null,
    },
    meta: {
      connectableAccount: false,
      connectableReview: false,
      connectableAccountStoreIdentifier: false,
    }
  })
  const isLoading = ref<boolean>(false)
  const isWriting = ref<boolean>(false)

  // 一覧取得 API
  const readConnects = async ({
    companyUuid, storeUuid,
  }: ReadConnectsApiParams) => {
    if (isLoading.value) return

    isLoading.value = true
    return axios
      .get(`/api/companies/${ companyUuid }/stores/${ storeUuid }/connects`)
      .then(res => {
        const formatValues = {
          items: res.data.data,
          meta: res.data.meta
        }
        connects.value = castAttributesForCamelCase(formatValues) as Connects
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  // 詳細取得 API
  const readConnect = async ({
    companyUuid, storeUuid, siteType,
  }: ReadConnectApiParams) => {
    if (isLoading.value) return

    isLoading.value = true
    return axios
      .get(`/api/companies/${ companyUuid }/stores/${ storeUuid }/connects/${ siteType }`)
      .then(res => {
        const { site_type, review, account } = res.data.data
        const formatValues = {
          site_type,
          review,
          account,
          meta: res.data.meta
        }
        connect.value = castAttributesForCamelCase(formatValues) as Connect
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  // 作成 API
  const createConnect = async ({
    companyUuid, storeUuid, siteType, params,
  }: CreateConnectApiParams) => {
    if (isWriting.value) return

    isWriting.value = true
    return axios
      .post(
        `/api/companies/${ companyUuid }/stores/${ storeUuid }/connects/${ siteType }`,
        castAttributesForSnakeCase(params)
      )
      .then(() => {
        updateFlash({ type: 'notice', message: t('models.locationConnect.created') })
        return 'success'
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
        return 'failure'
      })
      .finally(() => {
        isWriting.value = false
      })
  }

  // 更新 API
  const updateConnect = async ({
    companyUuid, storeUuid, siteType, params,
  }: UpdateConnectApiParams) => {
    if (isWriting.value) return

    isWriting.value = true
    return axios
      .patch(
        `/api/companies/${ companyUuid }/stores/${ storeUuid }/connects/${ siteType }`,
        castAttributesForSnakeCase(params)
      )
      .then(() => {
        updateFlash({ type: 'notice', message: t('models.locationConnect.updated') })
        return 'success'
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
        return 'failure'
      })
      .finally(() => {
        isWriting.value = false
      })
  }

  // 削除 API
  const deleteConnect = async ({
    companyUuid, storeUuid, connectType, params,
  }: DeleteConnectApiParams) => {
    if (isWriting.value) return

    isWriting.value = true
    return axios
      .delete(
        `/api/companies/${ companyUuid }/stores/${ storeUuid }/connects/${ connectType }`,
        { params: castAttributesForSnakeCase(params) },
      )
      .then(() => {
        updateFlash({ type: 'notice', message: t('models.locationConnect.deleted') })
        return 'success'
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
        return 'failure'
      })
      .finally(() => {
        isWriting.value = false
      })
  }

  return {
    connects,
    connect,
    isLoading: readonly(isLoading),
    isWriting: readonly(isWriting),
    readConnects,
    readConnect,
    createConnect,
    updateConnect,
    deleteConnect,
  }
})
